import { useState } from 'react';
import { Save, Globe, Sun, Moon, Bell, Shield, Store } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';

export default function AdminSettings() {
  const { t, theme, toggleTheme, lang, setLang } = useSettings();
  const [store, setStore] = useState({ name: 'TechStore', email: 'admin@techstore.com', phone: '+216 74 000 000', address: 'Sfax, Tunisia', currency: 'USD', taxRate: 19, freeShippingThreshold: 500, shippingCost: 15 });
  const [notifications, setNotifications] = useState({ newOrder: true, lowStock: true, newUser: false, newReview: true });
  const [saving, setSaving] = useState(false);

  const sf = (k, v) => setStore(p => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    setSaving(false);
    toast.success('Settings saved!');
  };

  const LANGS = [{ code: 'en', label: 'English', flag: '🇬🇧' }, { code: 'fr', label: 'Français', flag: '🇫🇷' }, { code: 'ar', label: 'العربية', flag: '🇸🇦' }];

  return (
    <AdminLayout title={t('admin', 'settings')}>
      <div className="admin-settings-wrap" style={{ maxWidth: 800 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, marginBottom: 24 }}>{t('admin', 'settings')}</h2>

        <form onSubmit={handleSave}>
          {/* Store info */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div style={{ width: 40, height: 40, background: 'var(--accent-dim)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Store size={20} color="var(--accent)" />
              </div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>Store Information</h3>
            </div>
            <div className="grid-2">
              {[['Store Name', 'name', 'text'], ['Contact Email', 'email', 'email'], ['Phone', 'phone', 'text'], ['Address', 'address', 'text']].map(([label, key, type]) => (
                <div key={key} className="form-group" style={{ gridColumn: key === 'address' ? '1/-1' : undefined }}>
                  <label className="form-label">{label}</label>
                  <input className="form-input" type={type} value={store[key]} onChange={e => sf(key, e.target.value)} />
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Currency</label>
                <select className="form-input" value={store.currency} onChange={e => sf('currency', e.target.value)}>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="TND">TND (DT)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Tax Rate (%)</label>
                <input className="form-input" type="number" min="0" max="99" value={store.taxRate} onChange={e => sf('taxRate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Free Shipping Threshold ($)</label>
                <input className="form-input" type="number" min="0" value={store.freeShippingThreshold} onChange={e => sf('freeShippingThreshold', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Shipping Cost ($)</label>
                <input className="form-input" type="number" min="0" value={store.shippingCost} onChange={e => sf('shippingCost', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Appearance */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div style={{ width: 40, height: 40, background: 'var(--gold-dim)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {theme === 'dark' ? <Moon size={20} color="var(--gold)" /> : <Sun size={20} color="var(--gold)" />}
              </div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>Appearance & Language</h3>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
              {['dark', 'light'].map(th => (
                <div key={th} onClick={() => th !== theme && toggleTheme()}
                  style={{ flex: 1, padding: 20, borderRadius: 16, border: `2px solid ${theme === th ? 'var(--accent)' : 'var(--border)'}`, background: theme === th ? 'var(--accent-dim)' : 'var(--bg-secondary)', cursor: 'pointer', textAlign: 'center', transition: 'var(--transition)' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>{th === 'dark' ? '🌙' : '☀️'}</div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: theme === th ? 'var(--accent)' : 'var(--text-secondary)', textTransform: 'capitalize' }}>{th} Mode</div>
                </div>
              ))}
            </div>
            <div className="form-label" style={{ marginBottom: 12 }}>Language</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {LANGS.map(l => (
                <button key={l.code} type="button" onClick={() => setLang(l.code)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 12, border: `2px solid ${lang === l.code ? 'var(--accent)' : 'var(--border)'}`, background: lang === l.code ? 'var(--accent-dim)' : 'var(--bg-secondary)', cursor: 'pointer', color: lang === l.code ? 'var(--accent)' : 'var(--text-primary)', fontWeight: lang === l.code ? 700 : 500, fontFamily: 'var(--font-body)', transition: 'var(--transition)' }}>
                  <span style={{ fontSize: 20 }}>{l.flag}</span> {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notifications */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <div style={{ width: 40, height: 40, background: 'rgba(56,189,248,0.12)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Bell size={20} color="var(--info)" />
              </div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700 }}>Notifications</h3>
            </div>
            {[['newOrder', 'New Order Placed', 'Get notified when a new order is placed'], ['lowStock', 'Low Stock Alert', 'Alert when product stock falls below 5'], ['newUser', 'New User Registration', 'Notify when a new user registers'], ['newReview', 'New Product Review', 'Notify when a product gets a new review']].map(([key, label, desc]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{desc}</div>
                </div>
                <div onClick={() => setNotifications(p => ({ ...p, [key]: !p[key] }))}
                  style={{ width: 48, height: 26, borderRadius: 99, background: notifications[key] ? 'var(--accent)' : 'var(--border-light)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ position: 'absolute', top: 3, left: notifications[key] ? 24 : 3, width: 20, height: 20, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
                </div>
              </div>
            ))}
          </div>

          <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
            <Save size={18} /> {saving ? 'Saving...' : t('common', 'save')}
          </button>
        </form>
      </div>
    </AdminLayout>
  );
}
